/**
 * GPP-202603 — Cliente del módulo «Servicio de Correo». Envuelve las Cloud Functions callable
 * `portal_correo*` (proxy admin al NOTIF-API). El navegador nunca habla directo con el servicio
 * ni ve el secreto: todo pasa por estas funciones, que verifican el rol admin en el backend.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

// ── Tipos compartidos ──────────────────────────────────────────────────────
export interface Indicadores {
  enviados: number; entregados: number; aperturasDetectadas: number; clics: number;
  rebotes: number; quejas: number; errores: number;
}
export interface Tasas {
  entrega: number; rebote: number; apertura: number; clic: number; clicSobreApertura: number;
}
export interface GrupoSerie { clave: string; indicadores: Indicadores; tasas: Tasas }

/** Serie ya etiquetada por el backend con el catálogo del servicio: `clave` es
 *  el identificador técnico y `etiqueta` el texto legible que se muestra. */
export interface GrupoSerieTipo extends GrupoSerie { etiqueta: string; esAutenticacion: boolean }
export interface GrupoSerieApp extends GrupoSerie { etiqueta: string; aplicacionId: number | null }

/** Un bucket horario (hora local de Panamá) del panel de actividad por hora.
 *  Lo arma el backend recorriendo el historial: el NOTIF-API no agrupa por hora. */
export interface PuntoHora {
  hora: string;
  enviados: number;
  entregados: number;
  rebotes: number;
  errores: number;
  porTipo: Record<string, number>;
}

export interface Estadisticas {
  indicadores: Indicadores;
  tasas: Tasas;
  serieDia: GrupoSerie[];
  serieAplicacion: GrupoSerieApp[];
  serieTipo: GrupoSerieTipo[];
  /** código de tipo → descripción legible, para etiquetar el mapa horario. */
  tiposEtiqueta: Record<string, string>;
  /** null cuando el historial no se pudo recorrer: la sección horaria se oculta
   *  y el resto del panel se sigue mostrando. */
  serieHora: PuntoHora[] | null;
  horaAnalizados: number;
  horaTotal: number;
  /** true si se alcanzó el techo de correos analizados y la serie es parcial. */
  horaTruncado: boolean;
}

export interface FiltrosEstadisticas {
  desde?: string; hasta?: string; aplicacion?: number; tipo?: string; plantilla?: string;
}

export interface CorreoResumen {
  envioId: string; tipo: string; estado: string; asunto: string;
  fechaRecibidoUtc: string; para: string[];
}
export interface PaginaHistorial { total: number; pagina: number; tamano: number; items: CorreoResumen[] }
export interface FiltrosHistorial {
  to?: string; desde?: string; hasta?: string; estado?: string; tipo?: string;
  pagina?: number; tamano?: number;
}

export interface EventoCorreo { tipo: string; fechaUtc: string }
export interface DestinatarioCorreo { direccion: string; tipo: string; suprimido: boolean }
export interface CorreoDetalle {
  envioId: string; tipo: string; clase: string; estado: string; asunto: string;
  sesMessageId: string | null; intentos: number; motivoFallo: string | null;
  fechaRecibidoUtc: string; fechaEnviadoUtc: string | null; ventana: string | null;
  destinatarios: DestinatarioCorreo[]; eventos: EventoCorreo[];
  cuerpo: { cuerpoHtml: string | null; cuerpoTexto: string | null } | null;
}

export interface TipoCorreo {
  tipoCorreoId: number; codigo: string; descripcion: string; esAutenticacion: boolean;
}
export interface Plantilla {
  plantillaId: number; codigo: string; version: number; asunto: string;
  habilitada: boolean; variablesJson: string;
}
export interface Catalogos { tipos: TipoCorreo[]; plantillas: Plantilla[] }

export interface Aplicacion {
  aplicacionId: number; clientId: string; nombre: string; nombreRemitente: string;
  esAdmin: boolean; activa: boolean; limitePorMinuto: number; permisos: string[];
}
export interface AplicacionesResp { aplicaciones: Aplicacion[]; tipos: TipoCorreo[] }

export interface NoEntregable {
  envioId: string; tipo: string; asunto: string; intentos: number;
  motivoFallo: string | null; fechaRecibidoUtc: string;
}

export interface RegistroAuditoria {
  auditoriaId: number; clientId: string; accion: string; recurso: string | null;
  resultado: string; detalleJson: string | null; fechaUtc: string;
}
export interface PaginaAuditoria { total: number; pagina: number; tamano: number; items: RegistroAuditoria[] }
export interface FiltrosAuditoria {
  desde?: string; hasta?: string; clientId?: string; pagina?: number; tamano?: number;
}

// ── Envoltorio genérico ─────────────────────────────────────────────────────
function llamar<TReq, TRes>(nombre: string) {
  const fn = httpsCallable<TReq, TRes>(functions, nombre);
  return async (data?: TReq): Promise<TRes> => (await fn(data ?? ({} as TReq))).data;
}

// ── Estadísticas (T-04) ──
export const obtenerEstadisticas = llamar<FiltrosEstadisticas, Estadisticas>('portal_correoEstadisticas');

// ── Historial (T-05) ──
export const obtenerHistorial = llamar<FiltrosHistorial, PaginaHistorial>('portal_correoHistorial');
export const obtenerDetalle = llamar<{ envioId: string }, CorreoDetalle>('portal_correoDetalle');

// ── Catálogos (T-06) ──
export const obtenerCatalogos = llamar<void, Catalogos>('portal_correoCatalogos');
export const crearTipo = llamar<{ codigo: string; descripcion: string; esAutenticacion?: boolean }, unknown>('portal_correoCrearTipo');
export const crearPlantilla = llamar<{
  codigo: string; asunto: string; cuerpoHtml: string; cuerpoTexto?: string;
  variables?: string[]; habilitar?: boolean;
}, unknown>('portal_correoCrearPlantilla');
export const habilitarPlantilla = llamar<{ id: number; habilitar: boolean }, { ok: boolean }>('portal_correoPlantillaHabilitar');

// ── Aplicaciones (T-06) ──
export const obtenerAplicaciones = llamar<void, AplicacionesResp>('portal_correoAplicaciones');
export const crearAplicacion = llamar<{
  clientId: string; nombre: string; nombreRemitente: string; esAdmin?: boolean; limitePorMinuto?: number;
}, unknown>('portal_correoCrearAplicacion');
export const actualizarAplicacion = llamar<{
  id: number; nombre?: string; nombreRemitente?: string; activa?: boolean; limitePorMinuto?: number;
}, { ok: boolean }>('portal_correoActualizarAplicacion');
export const guardarPermisos = llamar<{ id: number; tiposCorreo: string[] }, { ok: boolean }>('portal_correoPermisos');

// ── No entregables y auditoría (T-07) ──
export const obtenerNoEntregables = llamar<{ desde?: string; hasta?: string }, NoEntregable[]>('portal_correoNoEntregables');
export const reprocesar = llamar<{ envioId: string }, unknown>('portal_correoReprocesar');
export const obtenerAuditoria = llamar<FiltrosAuditoria, PaginaAuditoria>('portal_correoAuditoria');
